import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router-dom'
import { Plus, Send, Trash2, Pencil, Check, X, Copy, Settings as SettingsIcon } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

// remark-gfm gives us tables, strikethrough, task-lists, and autolinks —
// standard GitHub Flavored Markdown. Without it react-markdown renders
// tables as raw pipes.
const MD_PLUGINS = [remarkGfm]

// Shared markdown components — mirrors ChatPanel.tsx's styling so both
// chat surfaces render bold/lists/code/etc consistently. Extended with
// table styling since remark-gfm surfaces those nodes.
const MD_COMPONENTS = {
  p: (props: any) => <p className="mb-1.5 last:mb-0" {...props} />,
  ul: (props: any) => <ul className="my-1.5 list-disc pl-4 space-y-0.5" {...props} />,
  ol: (props: any) => <ol className="my-1.5 list-decimal pl-4 space-y-0.5" {...props} />,
  li: (props: any) => <li className="leading-snug" {...props} />,
  strong: (props: any) => <strong className="font-semibold" {...props} />,
  em: (props: any) => <em className="italic" {...props} />,
  code: (props: any) => (
    <code className="me-mono rounded bg-[color:var(--me-bg)] px-1 py-0.5 text-[11px]" {...props} />
  ),
  pre: (props: any) => (
    <pre className="me-mono my-2 max-h-[400px] overflow-auto rounded bg-[color:var(--me-bg)] p-2 text-[11px]" {...props} />
  ),
  h1: (props: any) => <div className="mb-1 mt-2 text-[14px] font-semibold" {...props} />,
  h2: (props: any) => <div className="mb-1 mt-2 text-[13px] font-semibold" {...props} />,
  h3: (props: any) => <div className="mb-1 mt-2 text-[13px] font-semibold" {...props} />,
  a: (props: any) => <a className="underline" target="_blank" rel="noreferrer" {...props} />,
  // Tables — wide tables scroll horizontally within their own container
  // so they never blow out the message-bubble width.
  table: (props: any) => (
    <div className="my-2 overflow-x-auto">
      <table className="min-w-full border-collapse text-[12px]" {...props} />
    </div>
  ),
  thead: (props: any) => <thead className="bg-[color:var(--me-bg)]" {...props} />,
  th: (props: any) => (
    <th className="border me-border px-2 py-1 text-left font-semibold" {...props} />
  ),
  td: (props: any) => (
    <td className="border me-border px-2 py-1 align-top" {...props} />
  ),
  hr: (props: any) => <hr className="my-3 border-t me-border" {...props} />,
  blockquote: (props: any) => (
    <blockquote className="my-2 border-l-2 me-border pl-3 italic me-text-mute" {...props} />
  ),
}
import {
  claudeChats,
  claudeChatSettings,
  type ClaudeChat,
  type ClaudeChatMessage,
} from '../../api'
import { useAuth } from '../../auth'


// ─── The tab ────────────────────────────────────────────────────────────

export default function ClaudeTab() {
  const auth = useAuth()
  const user = auth.status === 'authenticated' ? auth.user : null
  const qc = useQueryClient()
  const navigate = useNavigate()
  const { chatId: chatIdParam } = useParams<{ chatId?: string }>()

  // Chat id is URL-derived so every conversation has a shareable link
  // (/studio/claude/42). Backend filters strictly by request.user, so
  // pasting someone else's URL just returns 404 — no cross-user leak.
  const activeId = chatIdParam ? Number(chatIdParam) : null
  const setActiveId = (id: number | null) => {
    if (id == null) navigate('/studio/claude')
    else navigate(`/studio/claude/${id}`)
  }
  const [showSettings, setShowSettings] = useState(false)

  const chats = useQuery({
    queryKey: ['claudeChats'],
    queryFn: claudeChats.list,
  })
  // Fireworks shortlist rides along on the settings call (read-only). Drives the
  // engine/model picker for new chats. (Slice 1 — Ken 2026-10-17.)
  const settings = useQuery({ queryKey: ['claudeChatSettings'], queryFn: claudeChatSettings.get })
  const fwModels = settings.data?.fireworks_models || []
  const [newEngine, setNewEngine] = useState<'claude' | 'fireworks'>('claude')
  const [newModel, setNewModel] = useState('')
  // The engine picker is hidden until "New" is pressed; then the user chooses.
  const [choosing, setChoosing] = useState(false)

  useEffect(() => {
    // No chat in URL + we have some in the list → jump to the newest so
    // the pane isn't blank on first visit. `replace: true` so the empty
    // /studio/claude URL doesn't leave a stray back-button entry.
    if (activeId == null && chats.data && chats.data.length > 0) {
      navigate(`/studio/claude/${chats.data[0].id}`, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chats.data, activeId])

  const createChat = useMutation({
    mutationFn: (opts: { engine?: string; model?: string }) => claudeChats.create(opts),
    onSuccess: (chat) => {
      qc.invalidateQueries({ queryKey: ['claudeChats'] })
      setActiveId(chat.id)
      setChoosing(false)
    },
  })
  const startNewChat = () => createChat.mutate(
    newEngine === 'fireworks'
      ? { engine: 'fireworks', model: newModel || fwModels[0]?.id || '' }
      : { engine: 'claude' },
  )

  const deleteChat = useMutation({
    mutationFn: (id: number) => claudeChats.remove(id),
    onSuccess: (_v, id) => {
      qc.invalidateQueries({ queryKey: ['claudeChats'] })
      if (id === activeId) setActiveId(null)
    },
  })

  return (
    <div className="flex h-full">
      {/* Sidebar */}
      <aside className="flex w-72 shrink-0 flex-col border-r me-border bg-[color:var(--me-surface)]">
        <div className="space-y-2 border-b me-border px-3 py-2">
          {!choosing ? (
            // Default: just "New" — the engine picker stays hidden until pressed.
            <div className="flex items-center gap-2">
              <button
                onClick={() => setChoosing(true)}
                className="me-btn me-btn-primary flex items-center gap-1.5 text-[12px]"
              >
                <Plus size={13} /> New
              </button>
              {user?.is_superuser && (
                <button
                  onClick={() => setShowSettings(true)}
                  className="me-btn me-btn-outline ml-auto"
                  title="Boilerplate (Emerson only)"
                >
                  <SettingsIcon size={13} />
                </button>
              )}
            </div>
          ) : (
            // Pressed New → now choose the engine (and a Fireworks model) for THIS chat.
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <select
                  value={newEngine}
                  onChange={(e) => setNewEngine(e.target.value as 'claude' | 'fireworks')}
                  className="me-input text-[11px]"
                  title="Engine for this new chat"
                >
                  <option value="claude">Claude</option>
                  <option value="fireworks">Fireworks</option>
                </select>
                <button
                  onClick={startNewChat}
                  className="me-btn me-btn-primary flex items-center gap-1.5 text-[12px]"
                  disabled={createChat.isPending}
                >
                  <Plus size={13} /> Create
                </button>
                <button
                  onClick={() => setChoosing(false)}
                  className="me-btn me-btn-outline"
                  title="Cancel"
                  disabled={createChat.isPending}
                >
                  <X size={13} />
                </button>
              </div>
              {newEngine === 'fireworks' && fwModels.length > 0 && (
                <select
                  value={newModel || fwModels[0]?.id || ''}
                  onChange={(e) => setNewModel(e.target.value)}
                  className="me-input w-full text-[11px]"
                  title="Fireworks model for this new chat"
                >
                  {fwModels.map((m) => (
                    <option key={m.id} value={m.id}>{m.label}{m.reasoning ? ' · reasoning' : ''}</option>
                  ))}
                </select>
              )}
            </div>
          )}
        </div>
        <div className="me-scroll flex-1 overflow-y-auto">
          {(chats.data || []).map((chat) => (
            <ChatRow
              key={chat.id}
              chat={chat}
              active={chat.id === activeId}
              onClick={() => setActiveId(chat.id)}
              onDelete={() => {
                if (confirm(`Delete "${chat.title || 'untitled'}"?`)) deleteChat.mutate(chat.id)
              }}
            />
          ))}
          {(chats.data || []).length === 0 && !chats.isLoading && (
            <div className="me-text-mute px-3 py-4 text-[12px]">
              No conversations yet. Click "New" to start.
            </div>
          )}
        </div>
      </aside>

      {/* Main pane */}
      <main className="flex flex-1 flex-col overflow-hidden bg-[color:var(--me-bg)]">
        {activeId == null ? (
          <div className="me-text-mute grid h-full place-items-center text-[13px]">
            Pick a conversation on the left, or start a new one.
          </div>
        ) : (
          <ChatPane key={activeId} chatId={activeId} onTitleChanged={() => qc.invalidateQueries({ queryKey: ['claudeChats'] })} />
        )}
      </main>

      {showSettings && (
        <BoilerplateModal onClose={() => setShowSettings(false)} />
      )}
    </div>
  )
}


// ─── Sidebar row ─────────────────────────────────────────────────────────

function ChatRow({ chat, active, onClick, onDelete }: {
  chat: ClaudeChat
  active: boolean
  onClick: () => void
  onDelete: () => void
}) {
  return (
    <div
      onClick={onClick}
      className={
        'group flex cursor-pointer items-start gap-2 border-b me-border px-3 py-2 text-[12px] hover:bg-[color:var(--me-hover)] ' +
        (active ? 'bg-[color:var(--me-hover)]' : '')
      }
    >
      <div className="min-w-0 flex-1">
        <div className="truncate font-semibold">{chat.title || '(untitled)'}</div>
        <div className="me-text-mute truncate">
          {new Date(chat.last_activity_at).toLocaleString()}
        </div>
      </div>
      <button
        onClick={(e) => { e.stopPropagation(); onDelete() }}
        className="me-text-mute opacity-0 transition-opacity group-hover:opacity-100 hover:text-red-400"
        title="Delete conversation"
      >
        <Trash2 size={13} />
      </button>
    </div>
  )
}


// ─── Main chat pane ──────────────────────────────────────────────────────

type StreamState =
  | { kind: 'idle' }
  | { kind: 'sending'; buffer: string; toolCalls: Array<{ name: string; input_preview: string }>; startedAt: number }


function formatDuration(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds))
  return s < 60
    ? `${s}s`
    : `${Math.floor(s / 60)}m ${(s % 60).toString().padStart(2, '0')}s`
}


function ElapsedBadge({ startedAt }: { startedAt: number }) {
  const [now, setNow] = useState(Date.now())
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 500)
    return () => window.clearInterval(id)
  }, [])
  return <span className="me-mono">{formatDuration((now - startedAt) / 1000)}</span>
}


function ChatPane({ chatId, onTitleChanged }: { chatId: number; onTitleChanged: () => void }) {
  const qc = useQueryClient()
  // NOTE: input state deliberately lives inside <MessageInput/>, NOT
  // here. Ken saw multi-100ms typing lag on long transcripts because
  // every keystroke re-rendered ChatPane, which re-rendered every
  // MessageBubble (each ReactMarkdown re-parses a fresh tree). With
  // the input isolated in its own component, keystrokes only re-render
  // the textarea. `sendRef` receives a stable send() we pass down.
  const [streaming, setStreaming] = useState<StreamState>({ kind: 'idle' })
  // The user message the current in-flight turn was sent with. Rendered
  // as an optimistic bubble AT THE BOTTOM of the transcript while the
  // SSE is streaming — otherwise the just-typed message vanishes until
  // the turn finishes and history refetches. Cleared on send() finally.
  const [pendingUserMessage, setPendingUserMessage] = useState<string | null>(null)
  const scrollRef = useRef<HTMLDivElement>(null)

  const history = useQuery({
    queryKey: ['claudeChat', chatId, 'messages'],
    queryFn: () => claudeChats.messages(chatId),
    refetchOnWindowFocus: false,
    // While a turn is in progress server-side (JSONL modified recently),
    // poll every 4s so the user sees new content appear without having
    // to refresh — covers "I navigated away and came back mid-turn".
    // Also covers "I stayed on the tab but my SSE stream broke".
    refetchInterval: (q) =>
      (q.state.data as any)?.is_active ? 4000 : false,
  })

  const chatRow = useMemo(
    () => (qc.getQueryData<ClaudeChat[]>(['claudeChats']) || []).find((c) => c.id === chatId),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [chatId, history.data],
  )

  // Fireworks slots can switch model mid-conversation and continue — the JSONL
  // history is shared, so the next turn runs on the new model with full context.
  const settings = useQuery({ queryKey: ['claudeChatSettings'], queryFn: claudeChatSettings.get })
  const fwModels = settings.data?.fireworks_models || []
  const switchModel = useMutation({
    mutationFn: (model: string) => claudeChats.update(chatId, { model }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['claudeChats'] })
      qc.invalidateQueries({ queryKey: ['claudeChat', chatId, 'messages'] })
    },
  })

  // Track first data arrival for THIS chat so we can unconditionally
  // scroll to bottom on entry (users want to see the latest, not the
  // oldest). After that, only scroll on new content if the user is
  // ALREADY near the bottom — otherwise we'd yank them back down while
  // they're reading history.
  const didInitialScrollRef = useRef(false)
  useEffect(() => {
    didInitialScrollRef.current = false  // reset when switching chats
  }, [chatId])
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    if (!didInitialScrollRef.current && history.data) {
      // First render with data for this chat — always jump to bottom.
      el.scrollTop = el.scrollHeight
      didInitialScrollRef.current = true
      return
    }
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight
    if (distanceFromBottom < 200) {
      el.scrollTop = el.scrollHeight
    }
  }, [history.data, streaming])

  // useCallback so the memoized MessageInput doesn't re-render on every
  // ChatPane render — deps intentionally minimal.
  const send = useCallback(async (rawMessage: string) => {
    const message = (rawMessage || '').trim()
    if (!message || streaming.kind === 'sending') return
    setPendingUserMessage(message)
    setStreaming({ kind: 'sending', buffer: '', toolCalls: [], startedAt: Date.now() })
    try {
      // CSRF cookie should already be set by earlier requests; use fetch
      // directly so we can consume the SSE body stream.
      const csrf = getCookie('csrftoken') || ''
      const resp = await fetch(`/api/claude-chats/${chatId}/send/`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrf,
        },
        body: JSON.stringify({ message }),
      })
      if (resp.status === 409) {
        // Previous turn's subprocess is still running (survives SSE
        // disconnects now — see apps/claude_chat/subprocess_runner.py).
        // Tell the user to wait; the auto-poll will show progress.
        throw new Error(
          'Previous turn is still processing on the server. Watch the "Still working…" '
          + 'banner — it will finish and history will refresh automatically.'
        )
      }
      if (!resp.body) {
        throw new Error(`no body: HTTP ${resp.status}`)
      }
      const reader = resp.body.getReader()
      const decoder = new TextDecoder()
      let leftover = ''
      // Parse SSE frames. Each frame ends with a blank line.
      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        leftover += decoder.decode(value, { stream: true })
        const frames = leftover.split('\n\n')
        leftover = frames.pop() || ''
        for (const frame of frames) {
          const evLine = frame.split('\n').find((l) => l.startsWith('event: '))
          const dataLine = frame.split('\n').find((l) => l.startsWith('data: '))
          const ev = evLine ? evLine.slice(7).trim() : 'event'
          const data = dataLine ? dataLine.slice(6) : ''
          if (!data) continue
          try {
            const parsed = JSON.parse(data)
            handleStreamEvent(ev, parsed, setStreaming)
          } catch {
            // ignore non-JSON frames
          }
        }
      }
    } catch (e) {
      setStreaming((s) => s.kind === 'sending'
        ? { ...s, buffer: s.buffer + `\n\n_[error: ${(e as Error).message}]_` }
        : s)
    } finally {
      // Refetch history so the persistent JSONL becomes visible on the next render.
      await qc.invalidateQueries({ queryKey: ['claudeChat', chatId, 'messages'] })
      // Refresh sidebar (title / preview may have been auto-seeded).
      onTitleChanged()
      setStreaming({ kind: 'idle' })
      // Clear the optimistic user bubble — the refetched history now
      // contains the real one.
      setPendingUserMessage(null)
    }
  }, [streaming.kind, chatId, qc, onTitleChanged])

  return (
    <div className="flex h-full flex-col">
      {/* Header with editable title + engine/model */}
      <div className="flex items-center gap-3 border-b me-border bg-[color:var(--me-surface)] px-4 py-2">
        {chatRow && <EditableTitle chat={chatRow} onSaved={onTitleChanged} />}
        {history.data?.engine === 'fireworks' ? (
          <div className="ml-auto flex items-center gap-1.5 text-[11px] me-text-mute">
            <span className="font-semibold">Fireworks</span>
            <select
              value={history.data.model || fwModels[0]?.id || ''}
              onChange={(e) => switchModel.mutate(e.target.value)}
              className="me-input text-[11px]"
              disabled={switchModel.isPending}
              title="Switch model — continues the same conversation"
            >
              {fwModels.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
            </select>
          </div>
        ) : history.data?.engine === 'claude' ? (
          <span className="ml-auto text-[11px] me-text-mute">Claude</span>
        ) : null}
      </div>
      {/* Message scrollback */}
      <div ref={scrollRef} className="me-scroll flex-1 overflow-y-auto px-4 py-3">
        {(history.data?.messages || []).map((msg, i) => (
          <MessageBubble key={i} msg={msg} />
        ))}
        {/* Optimistic bubble for the message currently being processed.
            Hidden once refetched history includes it (last user message
            in history matches). Prevents "I sent it but it vanished". */}
        {pendingUserMessage && !(() => {
          const msgs = history.data?.messages || []
          for (let i = msgs.length - 1; i >= 0; i--) {
            if (msgs[i].role === 'user') return msgs[i].text.trim() === pendingUserMessage.trim()
          }
          return false
        })() && (
          <MessageBubble msg={{ role: 'user', text: pendingUserMessage }} />
        )}
        {streaming.kind === 'sending' && (
          <div className="my-3 rounded-md bg-[color:var(--me-hover)] px-3 py-2 text-[13px]">
            <div className="me-text-mute mb-1 flex items-center gap-2 text-[11px]">
              <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-[color:var(--me-fg)]" />
              <span>assistant · streaming</span>
              <span>·</span>
              <ElapsedBadge startedAt={streaming.startedAt} />
              {streaming.toolCalls.length > 0 && (
                <>
                  <span>·</span>
                  <span>{streaming.toolCalls.length} tool call{streaming.toolCalls.length === 1 ? '' : 's'}</span>
                </>
              )}
            </div>
            <div className="me-text break-words me-prose">
              {streaming.buffer
                ? <ReactMarkdown remarkPlugins={MD_PLUGINS} components={MD_COMPONENTS}>{streaming.buffer}</ReactMarkdown>
                : <span className="me-text-mute">…</span>}
            </div>
            {streaming.toolCalls.length > 0 && (
              <div className="me-text-mute mt-2 text-[11px]">
                Tools used this turn: {streaming.toolCalls.map((t) => t.name).join(', ')}
              </div>
            )}
          </div>
        )}
      </div>
      {/* Status banner — always visible while a turn is in flight, so the
          user doesn't have to scroll to know it's still working. */}
      {streaming.kind === 'sending' && (
        <div className="flex items-center gap-2 border-t me-border bg-[color:var(--me-accent-soft)] px-3 py-1.5 text-[12px]">
          <span className="inline-block h-2.5 w-2.5 animate-pulse rounded-full bg-[color:var(--me-fg)]" />
          <span className="font-semibold">Working…</span>
          <span className="me-mono text-[13px] font-semibold tabular-nums">
            <ElapsedBadge startedAt={streaming.startedAt} />
          </span>
          {streaming.toolCalls.length > 0 && (
            <span className="me-text-mute">
              · {streaming.toolCalls.length} tool call{streaming.toolCalls.length === 1 ? '' : 's'} so far
              {streaming.toolCalls.length > 0 && ` (${streaming.toolCalls[streaming.toolCalls.length - 1].name})`}
            </span>
          )}
        </div>
      )}
      {/* Server-side turn still active but we're NOT locally streaming —
          i.e. the user refreshed, navigated away and came back, or the
          SSE connection died mid-turn. Poll-refreshed every 4s (see
          the history useQuery). */}
      {streaming.kind === 'idle' && history.data?.is_active && (() => {
        // Elapsed since the current turn started = now - timestamp of
        // the last user message we can find in the transcript. Falls
        // back to seconds_since_last_write when no user turn is yet
        // written (rare — happens only for a moment right after send).
        const msgs = history.data.messages || []
        let lastUserTs: string | null = null
        for (let i = msgs.length - 1; i >= 0; i--) {
          if (msgs[i].role === 'user' && msgs[i].ts) { lastUserTs = msgs[i].ts as string; break }
        }
        const startedAt = lastUserTs ? Date.parse(lastUserTs) : null
        return (
          <div className="flex items-center gap-2 border-t me-border bg-[color:var(--me-accent-soft)] px-3 py-1.5 text-[12px]">
            <span className="inline-block h-2.5 w-2.5 animate-pulse rounded-full bg-[color:var(--me-fg)]" />
            <span className="font-semibold">Still working on the last turn…</span>
            {startedAt != null && !Number.isNaN(startedAt) && (
              <span className="me-mono text-[13px] font-semibold tabular-nums">
                <ElapsedBadge startedAt={startedAt} />
              </span>
            )}
            <span className="me-text-mute">
              · reconnected — live text stream ended, refreshing history every 4s
              {history.data.seconds_since_last_write != null && (
                <> · last activity {history.data.seconds_since_last_write < 1
                  ? '<1s'
                  : `${Math.round(history.data.seconds_since_last_write)}s`} ago</>
              )}
            </span>
          </div>
        )
      })()}
      {/* Input — isolated in its own component so keystrokes don't re-render
          the whole transcript. */}
      <MessageInput
        disabled={streaming.kind === 'sending'}
        onSend={send}
      />
    </div>
  )
}


// ─── One rendered message ────────────────────────────────────────────────

// Memoized so the parent re-rendering (e.g. streaming state change, poll
// refetch producing the same messages array) doesn't re-parse every
// markdown bubble. React.memo does a shallow compare on the msg prop.
const MessageBubble = memo(function MessageBubble({ msg }: { msg: ClaudeChatMessage }) {
  const isUser = msg.role === 'user'
  const [copied, setCopied] = useState(false)
  // Copy the whole response's raw markdown — saves hand-selecting long
  // answers (Ken 2026-08-19). Assistant messages only; user bubbles already
  // hold text they typed.
  const copyAll = useCallback(() => {
    if (!msg.text) return
    navigator.clipboard?.writeText(msg.text).then(() => {
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1500)
    }).catch(() => { /* clipboard blocked — leave the button as-is */ })
  }, [msg.text])
  return (
    <div className={'my-3 rounded-md px-3 py-2 text-[13px] ' + (isUser
      ? 'bg-[color:var(--me-accent-soft)]'
      : 'bg-[color:var(--me-hover)]')}>
      <div className="me-text-mute mb-1 text-[11px] flex items-center gap-2">
        <span>
          {msg.role}
          {msg.ts && <> · {new Date(msg.ts).toLocaleString()}</>}
          {msg.duration_seconds != null && msg.duration_seconds >= 0 && (
            <> · <span title="Time from your prompt to when Claude finished responding">took {formatDuration(msg.duration_seconds)}</span></>
          )}
        </span>
        {!isUser && msg.text && (
          <button
            type="button"
            onClick={copyAll}
            className="ml-auto inline-flex items-center gap-1 hover:me-text"
            title="Copy this entire response to the clipboard"
          >
            {copied ? <><Check size={11} /> copied</> : <><Copy size={11} /> copy</>}
          </button>
        )}
      </div>
      {msg.text && (
        <div className="me-text break-words me-prose">
          <ReactMarkdown remarkPlugins={MD_PLUGINS} components={MD_COMPONENTS}>{msg.text}</ReactMarkdown>
        </div>
      )}
      {msg.tool_calls && msg.tool_calls.length > 0 && (
        <ToolCallsDisclosure calls={msg.tool_calls} />
      )}
    </div>
  )
})


// Isolated textarea so keystrokes don't cascade a re-render into the
// transcript. Owns its own draft state; only calls back to the parent
// (via onSend) at submit time. `disabled` toggles both the textarea
// and the Send button, so the parent can prevent concurrent sends.
const MessageInput = memo(function MessageInput({
  disabled,
  onSend,
}: {
  disabled: boolean
  onSend: (message: string) => void
}) {
  const [draft, setDraft] = useState('')
  const submit = () => {
    const t = draft.trim()
    if (!t || disabled) return
    setDraft('')
    onSend(t)
  }
  return (
    <div className="border-t me-border bg-[color:var(--me-surface)] px-3 py-2">
      <div className="flex items-end gap-2">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            // Enter = send. Shift+Enter (or Alt/Meta/Ctrl+Enter) = newline.
            // Matches Slack/Linear/etc. conventions.
            if (e.key === 'Enter' && !e.shiftKey && !e.altKey && !e.metaKey && !e.ctrlKey) {
              e.preventDefault()
              submit()
            }
          }}
          placeholder="Ask Claude…  (Enter to send, Shift+Enter for newline)"
          rows={3}
          className="me-input flex-1 resize-none text-[13px]"
          disabled={disabled}
        />
        <button
          onClick={submit}
          className="me-btn me-btn-primary flex items-center gap-1.5"
          disabled={disabled || !draft.trim()}
        >
          <Send size={13} /> Send
        </button>
      </div>
    </div>
  )
})


// Click to expand a per-turn list of tool calls. Collapsed by default so
// the transcript stays readable; expanded shows each call's name + the
// input JSON (bash command, file path, etc.) that Claude sent to the tool.
function ToolCallsDisclosure({ calls }: { calls: NonNullable<ClaudeChatMessage['tool_calls']> }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="me-text-mute mt-2 text-[11px]">
      <button
        onClick={() => setOpen((v) => !v)}
        className="me-mono hover:underline"
      >
        {open ? '▾' : '▸'} {calls.length} tool call{calls.length === 1 ? '' : 's'} — {calls.map((t) => t.name).join(', ')}
      </button>
      {open && (
        <div className="mt-1 space-y-1">
          {calls.map((t, i) => (
            <div key={i} className="rounded border me-border bg-[color:var(--me-bg)] p-2">
              <div className="me-mono mb-1 font-semibold text-[11px]">{t.name}</div>
              <pre className="me-mono whitespace-pre-wrap break-all text-[11px] leading-snug">{t.input_preview}</pre>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}


// ─── Editable title ──────────────────────────────────────────────────────

function EditableTitle({ chat, onSaved }: { chat: ClaudeChat; onSaved: () => void }) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(chat.title || '')
  useEffect(() => { setDraft(chat.title || '') }, [chat.title])
  const mut = useMutation({
    mutationFn: (title: string) => claudeChats.updateTitle(chat.id, title),
    onSuccess: () => { setEditing(false); onSaved() },
  })
  if (!editing) {
    return (
      <div className="flex items-center gap-2">
        <div className="text-[14px] font-semibold">{chat.title || '(untitled)'}</div>
        <button onClick={() => setEditing(true)} className="me-text-mute hover:text-[color:var(--me-fg)]"><Pencil size={12} /></button>
      </div>
    )
  }
  return (
    <div className="flex items-center gap-2">
      <input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        className="me-input text-[14px]"
        autoFocus
      />
      <button onClick={() => mut.mutate(draft.trim() || '(untitled)')} className="me-btn me-btn-primary" disabled={mut.isPending}>
        <Check size={13} />
      </button>
      <button onClick={() => { setEditing(false); setDraft(chat.title || '') }} className="me-btn me-btn-outline">
        <X size={13} />
      </button>
    </div>
  )
}


// ─── Boilerplate modal (superuser only) ──────────────────────────────────

function BoilerplateModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient()
  const q = useQuery({ queryKey: ['claudeChatSettings'], queryFn: claudeChatSettings.get })
  const [draft, setDraft] = useState('')
  useEffect(() => {
    if (q.data) setDraft(q.data.boilerplate_text || '')
  }, [q.data])
  const mut = useMutation({
    mutationFn: (text: string) => claudeChatSettings.update({ boilerplate_text: text }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['claudeChatSettings'] }); onClose() },
  })
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40">
      <div className="w-[720px] max-w-[92vw] rounded-lg border me-border bg-[color:var(--me-surface)] p-4 shadow-xl">
        <div className="mb-3 flex items-center justify-between">
          <div className="text-[14px] font-semibold">Chat boilerplate</div>
          <button onClick={onClose} className="me-btn me-btn-outline"><X size={13} /></button>
        </div>
        <div className="me-text-mute mb-2 text-[12px]">
          Injected into every chat via <code>--append-system-prompt</code>. Ken
          doesn't see this; only you edit it. Use it for hard rules and reminders
          the model needs on every turn.
        </div>
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={18}
          className="me-input w-full font-mono text-[12px]"
        />
        <div className="mt-3 flex justify-end gap-2">
          <button onClick={onClose} className="me-btn me-btn-outline">Cancel</button>
          <button onClick={() => mut.mutate(draft)} disabled={mut.isPending} className="me-btn me-btn-primary">Save</button>
        </div>
      </div>
    </div>
  )
}


function handleStreamEvent(
  ev: string,
  payload: any,
  setStreaming: React.Dispatch<React.SetStateAction<StreamState>>,
): void {
  // The subprocess wrapper wraps each JSON event as {type, payload}.
  // We only care about a few event kinds — assistant text deltas and
  // completed tool calls. The rest inform state but don't render.
  if (ev === 'closed' || ev === 'done' || ev === 'error' || ev === 'start') return
  const inner = payload?.payload
  if (!inner) return
  const kind = inner.type
  if (kind === 'assistant') {
    const msg = inner.message || {}
    const content = msg.content || []
    if (Array.isArray(content)) {
      // Insert a paragraph break BETWEEN separate assistant events —
      // e.g. Assistant("let me check"), tool_use, tool_result,
      // Assistant("I found X") should read as two paragraphs, not
      // "let me checkI found X" glued together. Only prepend when the
      // buffer already has content AND doesn't already end in whitespace.
      const separator = '\n\n'
      let firstTextThisEvent = true
      for (const part of content) {
        if (part?.type === 'text' && typeof part.text === 'string') {
          const chunk = part.text
          setStreaming((s) => {
            if (s.kind !== 'sending') return s
            const needsSeparator = firstTextThisEvent
              && s.buffer.length > 0
              && !/\s$/.test(s.buffer)
              && !/^\s/.test(chunk)
            return { ...s, buffer: s.buffer + (needsSeparator ? separator : '') + chunk }
          })
          firstTextThisEvent = false
        } else if (part?.type === 'tool_use') {
          setStreaming((s) => s.kind === 'sending'
            ? { ...s, toolCalls: [...s.toolCalls, { name: part.name || '', input_preview: JSON.stringify(part.input || '').slice(0, 400) }] }
            : s)
        }
      }
    }
  }
}


function getCookie(name: string): string | null {
  const cookies = document.cookie.split(';').map((c) => c.trim())
  for (const c of cookies) {
    if (c.startsWith(name + '=')) return decodeURIComponent(c.slice(name.length + 1))
  }
  return null
}
